import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    AddRounded,
    CalendarMonthRounded,
    CloseRounded,
    DeleteOutlineRounded,
    EditRounded,
    EventBusyRounded,
    FactoryRounded,
    ImageRounded,
    RefreshRounded,
    SaveRounded,
    UploadFileRounded,
} from "@mui/icons-material";
import { Navigate } from "react-router-dom";
import EmbeddedLayout from "../../../components/admin/EmbeddedLayout";
import { baseURL } from "../../../config";
import { useUser } from "../../../hooks/useUser";
import nonWorkingDaysController, {
    IFactoryPayload,
    INonWorkingDays,
    INonWorkingFactory,
} from "../../../Api/nonWorkingDays";

type FactoryDraft = {
    breakStartDate: string;
    breakEndDate: string;
    orderDeadlineDate: string;
    message: string;
};

type NewFactoryDraft = {
    factoryName: string;
    image: File | null;
};

const emptyFactoryDraft = (): FactoryDraft => ({
    breakStartDate: "",
    breakEndDate: "",
    orderDeadlineDate: "",
    message: "",
});

const emptyNewFactoryDraft = (): NewFactoryDraft => ({
    factoryName: "",
    image: null,
});

const toInputDate = (value?: string | null) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toISOString().slice(0, 10);
};

const formatDate = (value?: string | null) => {
    if (!value) return "brak";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "brak";

    return new Intl.DateTimeFormat("pl-PL", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    }).format(date);
};

const getImageUrl = (image?: string) => {
    if (!image) return "";
    if (image.startsWith("http")) return image;
    return `${baseURL}${image.replace(/\\/g, "/")}`;
};

const isFactoryFilled = (factory: INonWorkingFactory) => Boolean(
    factory.breakStartDate ||
    factory.breakEndDate ||
    factory.orderDeadlineDate ||
    factory.message?.trim()
);

const sortFactories = (factories: INonWorkingFactory[]) => (
    [...factories].sort((a, b) => a.factoryName.localeCompare(b.factoryName, "pl"))
);

const toPayload = (factory: INonWorkingFactory, draft: FactoryDraft): IFactoryPayload => ({
    factoryName: factory.factoryName,
    breakStartDate: draft.breakStartDate,
    breakEndDate: draft.breakEndDate,
    orderDeadlineDate: draft.orderDeadlineDate,
    message: draft.message.trim(),
});

const NonWorkingDays = ({ embedded = false }: { embedded?: boolean }) => {
    const user = useUser();
    const isAdmin = user.hasPermission('nonWorkingDays.read');
    const authChecked = user.authReady;

    const [config, setConfig] = useState<INonWorkingDays | null>(null);
    const [eventDraft, setEventDraft] = useState({ hasUpcomingHoliday: false, holidayName: "" });
    const [newFactoryDraft, setNewFactoryDraft] = useState<NewFactoryDraft>(emptyNewFactoryDraft());
    const [selectedFactory, setSelectedFactory] = useState<INonWorkingFactory | null>(null);
    const [factoryDraft, setFactoryDraft] = useState<FactoryDraft>(emptyFactoryDraft());
    const [isAddFactoryOpen, setIsAddFactoryOpen] = useState(false);
    const [isFactoryEditMode, setIsFactoryEditMode] = useState(false);
    const [editedBaseFactory, setEditedBaseFactory] = useState<INonWorkingFactory | null>(null);
    const [baseFactoryDraft, setBaseFactoryDraft] = useState<NewFactoryDraft>(emptyNewFactoryDraft());
    const [newFactoryFileInputKey, setNewFactoryFileInputKey] = useState(0);
    const [newFactoryImagePreview, setNewFactoryImagePreview] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [endDialogOpen, setEndDialogOpen] = useState(false);
    const newFactoryFileInputRef = useRef<HTMLInputElement | null>(null);

    const load = useCallback(async () => {
        if (!authChecked || !isAdmin) return;

        setIsLoading(true);
        setError(null);

        const res = await nonWorkingDaysController.getNonWorkingDays();

        if (!res.ok || !res.nonWorkingDays) {
            setConfig(null);
            setError(res.message || "Nie udało się pobrać wolnego fabryk");
            setIsLoading(false);
            return;
        }

        setConfig(res.nonWorkingDays);
        setEventDraft({
            hasUpcomingHoliday: Boolean(res.nonWorkingDays.hasUpcomingHoliday),
            holidayName: res.nonWorkingDays.holidayName || "",
        });
        setIsLoading(false);
    }, [authChecked, isAdmin]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!newFactoryDraft.image) {
            setNewFactoryImagePreview("");
            return;
        }

        const previewUrl = URL.createObjectURL(newFactoryDraft.image);
        setNewFactoryImagePreview(previewUrl);

        return () => URL.revokeObjectURL(previewUrl);
    }, [newFactoryDraft.image]);

    const factories = config?.factories || [];

    const { waitingFactories, filledFactories } = useMemo(() => {
        const sorted = sortFactories(factories);

        return {
            waitingFactories: sorted.filter((factory) => !isFactoryFilled(factory)),
            filledFactories: sorted.filter(isFactoryFilled),
        };
    }, [factories]);

    if (authChecked && !isAdmin) {
        return <Navigate to="/logowanie" replace />;
    }

    const openFactoryModal = (factory: INonWorkingFactory) => {
        setSelectedFactory(factory);
        setFactoryDraft({
            breakStartDate: toInputDate(factory.breakStartDate),
            breakEndDate: toInputDate(factory.breakEndDate),
            orderDeadlineDate: toInputDate(factory.orderDeadlineDate),
            message: factory.message || "",
        });
        setError(null);
        setNotice(null);
    };

    const closeFactoryModal = () => {
        setSelectedFactory(null);
        setFactoryDraft(emptyFactoryDraft());
    };

    const openBaseFactoryEdit = (factory: INonWorkingFactory) => {
        setEditedBaseFactory(factory);
        setBaseFactoryDraft({
            factoryName: factory.factoryName,
            image: null,
        });
        setError(null);
        setNotice(null);
    };

    const closeBaseFactoryEdit = () => {
        setEditedBaseFactory(null);
        setBaseFactoryDraft(emptyNewFactoryDraft());
    };

    const resetNewFactoryForm = () => {
        setNewFactoryDraft(emptyNewFactoryDraft());
        setNewFactoryFileInputKey((current) => current + 1);

        if (newFactoryFileInputRef.current) {
            newFactoryFileInputRef.current.value = "";
        }
    };

    const clearNewFactoryImage = () => {
        setNewFactoryDraft((current) => ({ ...current, image: null }));
        setNewFactoryFileInputKey((current) => current + 1);

        if (newFactoryFileInputRef.current) {
            newFactoryFileInputRef.current.value = "";
        }
    };

    const saveEvent = async () => {
        setIsSaving(true);
        setError(null);
        setNotice(null);

        const res = await nonWorkingDaysController.updateHolidayEvent({
            hasUpcomingHoliday: eventDraft.hasUpcomingHoliday,
            holidayName: eventDraft.holidayName.trim(),
        });

        if (!res.ok || !res.nonWorkingDays) {
            setError(res.message || "Nie zapisano wydarzenia");
            setIsSaving(false);
            return;
        }

        setConfig(res.nonWorkingDays);
        setNotice("Zapisano ustawienia wydarzenia");
        setIsSaving(false);
    };

    const confirmEndEvent = async () => {
        setIsSaving(true);
        setError(null);
        setNotice(null);

        const res = await nonWorkingDaysController.endHolidayEvent();

        if (!res.ok || !res.nonWorkingDays) {
            setError(res.message || "Nie zakończono wydarzenia");
            setIsSaving(false);
            return;
        }

        setConfig(res.nonWorkingDays);
        setEventDraft({ hasUpcomingHoliday: false, holidayName: "" });
        closeFactoryModal();
        setEndDialogOpen(false);
        setNotice("Zakończono wydarzenie i wyczyszczono fabryki");
        setIsSaving(false);
    };

    const addFactory = async () => {
        const factoryName = newFactoryDraft.factoryName.trim();

        if (!factoryName) {
            setError("Podaj nazwę fabryki");
            return;
        }

        setIsSaving(true);
        setError(null);
        setNotice(null);

        const res = await nonWorkingDaysController.addFactory({
            factoryName,
            image: newFactoryDraft.image,
        });

        if (!res.ok || !res.nonWorkingDays) {
            setError(res.message || "Nie dodano fabryki");
            setIsSaving(false);
            return;
        }

        setConfig(res.nonWorkingDays);
        resetNewFactoryForm();
        setNotice("Dodano fabrykę do bazy");
        setIsSaving(false);
    };

    const saveBaseFactory = async () => {
        if (!editedBaseFactory) return;

        const factoryName = baseFactoryDraft.factoryName.trim();
        if (!factoryName) {
            setError("Podaj nazwę fabryki");
            return;
        }

        setIsSaving(true);
        setError(null);
        setNotice(null);

        const res = await nonWorkingDaysController.editFactory(editedBaseFactory._id, {
            factoryName,
            image: baseFactoryDraft.image,
        });

        if (!res.ok || !res.nonWorkingDays) {
            setError(res.message || "Nie zapisano fabryki");
            setIsSaving(false);
            return;
        }

        setConfig(res.nonWorkingDays);
        closeBaseFactoryEdit();
        setNotice("Zapisano fabrykę");
        setIsSaving(false);
    };

    const saveFactoryEventData = async () => {
        if (!selectedFactory) return;

        setIsSaving(true);
        setError(null);
        setNotice(null);

        const res = await nonWorkingDaysController.editFactory(
            selectedFactory._id,
            toPayload(selectedFactory, factoryDraft)
        );

        if (!res.ok || !res.nonWorkingDays) {
            setError(res.message || "Nie zapisano danych fabryki");
            setIsSaving(false);
            return;
        }

        setConfig(res.nonWorkingDays);
        closeFactoryModal();
        setNotice("Zapisano dane fabryki dla wydarzenia");
        setIsSaving(false);
    };

    const clearFactoryEventData = async () => {
        if (!selectedFactory) return;

        setIsSaving(true);
        setError(null);
        setNotice(null);

        const res = await nonWorkingDaysController.editFactory(selectedFactory._id, {
            factoryName: selectedFactory.factoryName,
            breakStartDate: "",
            breakEndDate: "",
            orderDeadlineDate: "",
            message: "",
        });

        if (!res.ok || !res.nonWorkingDays) {
            setError(res.message || "Nie wyczyszczono danych fabryki");
            setIsSaving(false);
            return;
        }

        setConfig(res.nonWorkingDays);
        closeFactoryModal();
        setNotice("Fabryka wróciła na listę po lewej");
        setIsSaving(false);
    };

    const deleteFactory = async (factory: INonWorkingFactory) => {
        const confirmed = window.confirm(`Usunąć fabrykę "${factory.factoryName}" z bazy?`);
        if (!confirmed) return;

        setIsSaving(true);
        setError(null);
        setNotice(null);

        const res = await nonWorkingDaysController.deleteFactory(factory._id);

        if (!res.ok) {
            setError(res.message || "Nie usunięto fabryki");
            setIsSaving(false);
            return;
        }

        await load();
        setNotice("Usunięto fabrykę");
        setIsSaving(false);
    };

    const handleAddFactoryKeyDown = (event: React.KeyboardEvent) => {
        if (event.key !== "Enter" || isSaving || isLoading) return;
        event.preventDefault();
        addFactory();
    };

    const handleBaseFactoryEditKeyDown = (event: React.KeyboardEvent) => {
        if (event.key !== "Enter" || isSaving) return;
        event.preventDefault();
        saveBaseFactory();
    };

    const handleFactoryEventKeyDown = (event: React.KeyboardEvent) => {
        if (event.key !== "Enter" || isSaving) return;

        const target = event.target as HTMLElement;
        if (target.tagName === "TEXTAREA" && !event.ctrlKey && !event.metaKey) return;

        event.preventDefault();
        saveFactoryEventData();
    };

    const renderFactoryButton = (factory: INonWorkingFactory, compact = false) => {
        const imageUrl = getImageUrl(factory.image);

        return (
            <Paper
                key={factory._id}
                elevation={0}
                onClick={() => openFactoryModal(factory)}
                sx={{
                    p: compact ? 1 : 1.25,
                    borderRadius: 1.5,
                    border: "1px solid #e5e7eb",
                    cursor: "pointer",
                    bgcolor: "#fff",
                    transition: "border-color .15s, transform .15s, box-shadow .15s",
                    "&:hover": {
                        borderColor: "#0f766e",
                        boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
                        transform: "translateY(-1px)",
                    },
                }}
            >
                <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
                    <Avatar
                        src={imageUrl}
                        variant="rounded"
                        sx={{
                            width: compact ? 34 : 42,
                            height: compact ? 34 : 42,
                            borderRadius: 1.25,
                            bgcolor: imageUrl ? "#fff" : "#eef2f7",
                            "& img": {
                                objectFit: "contain",
                                p: 0.35,
                            },
                        }}
                    >
                        <FactoryRounded fontSize="small" />
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontWeight: 850, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {factory.factoryName}
                        </Typography>
                    </Box>
                    {isFactoryEditMode ? (
                        <Stack direction="row" gap={0.25}>
                            <Tooltip title="Edytuj fabrykę">
                                <IconButton
                                    size="small"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        openBaseFactoryEdit(factory);
                                    }}
                                >
                                    <EditRounded fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Usuń fabrykę z bazy">
                                <IconButton
                                    size="small"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        deleteFactory(factory);
                                    }}
                                    sx={{ color: "#b91c1c" }}
                                >
                                    <DeleteOutlineRounded fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    ) : null}
                </Stack>
            </Paper>
        );
    };

    return (
        <EmbeddedLayout embedded={embedded} title="Wolne fabryk">
            <Box sx={{ minHeight: "100vh", bgcolor: "#f5f7fb", px: { xs: 1.5, md: 3 }, py: { xs: 2, md: 3 } }}>
                <Stack spacing={2} sx={{ maxWidth: 1440, mx: "auto" }}>
                    <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, border: "1px solid #e5e7eb", bgcolor: "#fff" }}>
                        <Stack direction={{ xs: "column", lg: "row" }} justifyContent="space-between" alignItems={{ lg: "center" }} gap={2}>
                            <Stack direction="row" alignItems="center" gap={1.5}>
                                <Box sx={{ width: 52, height: 52, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: "#0f766e", color: "#fff" }}>
                                    <FactoryRounded />
                                </Box>
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 950, color: "#0f172a", letterSpacing: 0 }}>
                                        Wolne fabryk
                                    </Typography>
                                </Box>
                            </Stack>

                            <Stack direction={{ xs: "column", md: "row" }} gap={1} alignItems={{ md: "center" }}>
                                <Tooltip title="Odśwież">
                                    <span>
                                        <IconButton onClick={load} disabled={isLoading || isSaving} sx={{ border: "1px solid #e2e8f0" }}>
                                            <RefreshRounded />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                        </Stack>
                    </Paper>

                    {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}
                    {notice ? <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert> : null}

                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", bgcolor: "#fff" }}>
                        <Stack direction={{ xs: "column", lg: "row" }} gap={1.5} alignItems={{ lg: "center" }} justifyContent="space-between">
                            <Stack direction="row" gap={1.25} alignItems="center">
                                <Switch
                                    checked={eventDraft.hasUpcomingHoliday}
                                    onChange={(event) => setEventDraft((current) => ({ ...current, hasUpcomingHoliday: event.target.checked }))}
                                />
                                <TextField
                                    label="Nazwa aktywnego wydarzenia"
                                    value={eventDraft.holidayName}
                                    onChange={(event) => setEventDraft((current) => ({ ...current, holidayName: event.target.value }))}
                                    placeholder="Np. Przerwa świąteczna"
                                    size="small"
                                    sx={{ minWidth: { xs: "100%", sm: 360 } }}
                                />
                            </Stack>

                            <Stack direction="row" gap={1} flexWrap="wrap">
                                <Button variant="contained" startIcon={<SaveRounded />} onClick={saveEvent} disabled={isSaving || isLoading} sx={{ borderRadius: 2, fontWeight: 850 }}>
                                    Zapisz wydarzenie
                                </Button>
                                <Button variant="outlined" color="error" startIcon={<EventBusyRounded />} onClick={() => setEndDialogOpen(true)} disabled={isSaving || isLoading} sx={{ borderRadius: 2, fontWeight: 850 }}>
                                    Koniec wydarzenia
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>

                    <Stack direction={{ xs: "column", lg: "row" }} gap={2} alignItems="stretch">
                        <Paper elevation={0} sx={{ width: { xs: "100%", lg: 340 }, p: 1.5, borderRadius: 2, border: "1px solid #e5e7eb", bgcolor: "#fbfdff" }}>
                            <Stack spacing={1.5}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                                    <Typography sx={{ fontWeight: 950, color: "#0f172a" }}>Baza fabryk</Typography>
                                    <Stack direction="row" alignItems="center" gap={0.75}>
                                        <Stack direction="row" alignItems="center" gap={0.25}>
                                            <Typography variant="caption" sx={{ fontWeight: 800, color: isFactoryEditMode ? "#0f766e" : "#64748b" }}>
                                                Edycja
                                            </Typography>
                                            <Switch
                                                size="small"
                                                checked={isFactoryEditMode}
                                                onChange={(event) => setIsFactoryEditMode(event.target.checked)}
                                            />
                                        </Stack>
                                        <Tooltip title="Dodaj fabrykę">
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    if (isAddFactoryOpen) {
                                                        resetNewFactoryForm();
                                                    }
                                                    setIsAddFactoryOpen((current) => !current);
                                                }}
                                                sx={{ border: "1px solid #dbe3ef", bgcolor: isAddFactoryOpen ? "#eef6f3" : "#fff" }}
                                            >
                                                {isAddFactoryOpen ? <CloseRounded fontSize="small" /> : <AddRounded fontSize="small" />}
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </Stack>

                                {isAddFactoryOpen ? (
                                    <Paper elevation={0} onKeyDown={handleAddFactoryKeyDown} sx={{ p: 1.25, borderRadius: 1.5, border: "1px solid #e5e7eb", bgcolor: "#fff" }}>
                                        <Stack spacing={1}>
                                            <TextField
                                                label="Nowa fabryka"
                                                size="small"
                                                value={newFactoryDraft.factoryName}
                                                onChange={(event) => setNewFactoryDraft((current) => ({ ...current, factoryName: event.target.value }))}
                                                fullWidth
                                            />
                                            <Button component="label" variant="outlined" size="small" startIcon={<UploadFileRounded />} sx={{ justifyContent: "flex-start", borderRadius: 1.5, fontWeight: 800 }}>
                                                {newFactoryDraft.image ? newFactoryDraft.image.name : "Zdjęcie"}
                                                <input
                                                    key={newFactoryFileInputKey}
                                                    ref={newFactoryFileInputRef}
                                                    hidden
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(event) => setNewFactoryDraft((current) => ({ ...current, image: event.target.files?.[0] || null }))}
                                                />
                                            </Button>
                                            {newFactoryImagePreview ? (
                                                <Paper elevation={0} sx={{ p: 1, borderRadius: 1.5, border: "1px solid #e5e7eb", bgcolor: "#fff" }}>
                                                    <Stack direction="row" alignItems="center" gap={1}>
                                                        <Avatar
                                                            src={newFactoryImagePreview}
                                                            variant="rounded"
                                                            sx={{
                                                                width: 56,
                                                                height: 56,
                                                                borderRadius: 1.5,
                                                                bgcolor: "#fff",
                                                                "& img": {
                                                                    objectFit: "contain",
                                                                    p: 0.5,
                                                                },
                                                            }}
                                                        >
                                                            <FactoryRounded />
                                                        </Avatar>
                                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                                            <Typography variant="caption" sx={{ display: "block", fontWeight: 800, color: "#64748b" }}>
                                                                Podglad zdjecia
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 800, color: "#0f172a" }}>
                                                                {newFactoryDraft.image?.name}
                                                            </Typography>
                                                        </Box>
                                                        <Tooltip title="Usun zdjecie">
                                                            <IconButton size="small" onClick={clearNewFactoryImage} sx={{ color: "#b91c1c" }}>
                                                                <CloseRounded fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </Paper>
                                            ) : null}
                                            <Button variant="contained" size="small" startIcon={<AddRounded />} onClick={addFactory} disabled={isSaving || isLoading} sx={{ borderRadius: 1.5, fontWeight: 900 }}>
                                                Dodaj
                                            </Button>
                                        </Stack>
                                    </Paper>
                                ) : null}

                                <Divider />

                                {isLoading && !config ? (
                                    <Box sx={{ py: 5, display: "grid", placeItems: "center" }}>
                                        <CircularProgress />
                                    </Box>
                                ) : waitingFactories.length === 0 ? (
                                    <Paper elevation={0} sx={{ p: 2, border: "1px dashed #cbd5e1", borderRadius: 1.5, textAlign: "center", bgcolor: "#f8fafc" }}>
                                        <Typography sx={{ fontWeight: 850 }}>Brak fabryk</Typography>
                                    </Paper>
                                ) : (
                                    <Stack spacing={0.75} sx={{ maxHeight: 616, overflow: "auto", pr: 0.5, pt: 0.5 }}>
                                        {waitingFactories.map((factory) => renderFactoryButton(factory, true))}
                                    </Stack>
                                )}
                            </Stack>
                        </Paper>

                        <Paper elevation={0} sx={{ flex: 1, borderRadius: 2, border: "1px solid #e5e7eb", bgcolor: "#fff", overflow: "hidden" }}>
                            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} gap={1} sx={{ p: 2 }}>
                                <Box>
                                    <Typography sx={{ fontWeight: 950, color: "#0f172a" }}>Fabryki w aktualnym wydarzeniu</Typography>
                                </Box>
                                {(isLoading || isSaving) ? <CircularProgress size={24} /> : null}
                            </Stack>
                            <Divider />

                            {filledFactories.length === 0 ? (
                                <Box sx={{ p: { xs: 2, md: 5 } }}>
                                    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: "1px dashed #cbd5e1", bgcolor: "#f8fafc", textAlign: "center" }}>
                                        <CalendarMonthRounded sx={{ fontSize: 44, color: "#64748b", mb: 1 }} />
                                        <Typography variant="h6" sx={{ fontWeight: 950 }}>Brak fabryk</Typography>
                                    </Paper>
                                </Box>
                            ) : (
                                <Box sx={{ p: 1.5, display: "grid", gridTemplateColumns: "1fr", gap: 1.25 }}>
                                    {filledFactories.map((factory) => {
                                        const imageUrl = getImageUrl(factory.image);

                                        return (
                                            <Paper
                                                key={factory._id}
                                                elevation={0}
                                                onClick={() => openFactoryModal(factory)}
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    border: "1px solid #e5e7eb",
                                                    cursor: "pointer",
                                                    "&:hover": { borderColor: "#2563eb", boxShadow: "0 12px 28px rgba(15, 23, 42, 0.08)" },
                                                }}
                                            >
                                                <Stack direction="row" gap={1.5}>
                                                    <Avatar
                                                        src={imageUrl}
                                                        variant="rounded"
                                                        sx={{
                                                            width: 72,
                                                            height: 72,
                                                            borderRadius: 2,
                                                            bgcolor: imageUrl ? "#fff" : "#eef2f7",
                                                            "& img": {
                                                                objectFit: "contain",
                                                                p: 0.75,
                                                            },
                                                        }}
                                                    >
                                                        <FactoryRounded />
                                                    </Avatar>
                                                    <Stack spacing={1} sx={{ minWidth: 0, flex: 1 }}>
                                                        <Stack direction="row" justifyContent="space-between" gap={1}>
                                                            <Typography sx={{ fontWeight: 950, color: "#0f172a", wordBreak: "break-word" }}>
                                                                {factory.factoryName}
                                                            </Typography>
                                                            {imageUrl ? <ImageRounded sx={{ color: "#64748b" }} fontSize="small" /> : null}
                                                        </Stack>
                                                        <Stack direction="row" gap={0.75} flexWrap="wrap">
                                                            <Chip size="small" label={`wolne: ${formatDate(factory.breakStartDate)} - ${formatDate(factory.breakEndDate)}`} />
                                                            <Chip size="small" label={`zamówienia do: ${formatDate(factory.orderDeadlineDate)}`} />
                                                        </Stack>
                                                        {factory.message ? (
                                                            <Typography variant="body2" sx={{ color: "#334155", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                                                {factory.message}
                                                            </Typography>
                                                        ) : null}
                                                    </Stack>
                                                </Stack>
                                            </Paper>
                                        );
                                    })}
                                </Box>
                            )}
                        </Paper>
                    </Stack>
                </Stack>
            </Box>

            <Dialog open={Boolean(editedBaseFactory)} onClose={() => !isSaving && closeBaseFactoryEdit()} fullWidth maxWidth="xs">
                <DialogTitle sx={{ pb: 1 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                        <Typography sx={{ fontWeight: 950 }}>Edytuj fabrykę</Typography>
                        <IconButton onClick={closeBaseFactoryEdit} disabled={isSaving}>
                            <CloseRounded />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent onKeyDown={handleBaseFactoryEditKeyDown}>
                    <Stack spacing={1.5} sx={{ pt: 1 }}>
                        <TextField
                            label="Nazwa fabryki"
                            value={baseFactoryDraft.factoryName}
                            onChange={(event) => setBaseFactoryDraft((current) => ({ ...current, factoryName: event.target.value }))}
                            fullWidth
                            autoFocus
                        />
                        <Button component="label" variant="outlined" startIcon={<UploadFileRounded />} sx={{ justifyContent: "flex-start", borderRadius: 2, fontWeight: 800 }}>
                            {baseFactoryDraft.image ? baseFactoryDraft.image.name : "Zmień zdjęcie"}
                            <input
                                hidden
                                type="file"
                                accept="image/*"
                                onChange={(event) => setBaseFactoryDraft((current) => ({ ...current, image: event.target.files?.[0] || null }))}
                            />
                        </Button>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
                    <Button variant="outlined" onClick={closeBaseFactoryEdit} disabled={isSaving}>
                        Anuluj
                    </Button>
                    <Button variant="contained" startIcon={<SaveRounded />} onClick={saveBaseFactory} disabled={isSaving} sx={{ fontWeight: 850 }}>
                        Zapisz
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={endDialogOpen} onClose={() => !isSaving && setEndDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ pb: 1 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                        <Stack direction="row" alignItems="center" gap={1.25}>
                            <Box
                                sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 2,
                                    display: "grid",
                                    placeItems: "center",
                                    bgcolor: "#fef2f2",
                                    color: "#b91c1c",
                                }}
                            >
                                <EventBusyRounded />
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: 950, color: "#0f172a" }}>
                                    Zakończyć wydarzenie?
                                </Typography>
                            </Box>
                        </Stack>
                        <IconButton onClick={() => setEndDialogOpen(false)} disabled={isSaving}>
                            <CloseRounded />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent onKeyDown={handleFactoryEventKeyDown}>
                    <Stack spacing={1.5} sx={{ pt: 1 }}>
                        <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: "1px solid #fee2e2", bgcolor: "#fff7f7" }}>
                            <Typography sx={{ fontWeight: 850, color: "#7f1d1d", mb: 0.5 }}>
                                Zostaną wyczyszczone dane wydarzenia
                            </Typography>
                        </Paper>

                        <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
                            <Paper elevation={0} sx={{ flex: 1, p: 1.5, borderRadius: 2, border: "1px solid #e5e7eb", bgcolor: "#f8fafc" }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                                    Wyczyści się
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 950 }}>
                                    {filledFactories.length}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    fabryk po prawej stronie
                                </Typography>
                            </Paper>
                            <Paper elevation={0} sx={{ flex: 1, p: 1.5, borderRadius: 2, border: "1px solid #dcfce7", bgcolor: "#f0fdf4" }}>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: "#166534" }}>
                                    Zostaje w bazie
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 950, color: "#166534" }}>
                                    {factories.length}
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#166534" }}>
                                    fabryk oraz zdjęcia
                                </Typography>
                            </Paper>
                        </Stack>

                        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #e5e7eb" }}>
                            <Typography variant="body2" color="text.secondary">
                                Fabryki wrócą do listy po lewej. Zdjęcia zostają.
                            </Typography>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
                    <Button variant="outlined" onClick={() => setEndDialogOpen(false)} disabled={isSaving} sx={{ borderRadius: 2 }}>
                        Anuluj
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={isSaving ? <CircularProgress color="inherit" size={18} /> : <EventBusyRounded />}
                        onClick={confirmEndEvent}
                        disabled={isSaving}
                        sx={{ borderRadius: 2, fontWeight: 900 }}
                    >
                        Zakończ wydarzenie
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(selectedFactory)} onClose={closeFactoryModal} fullWidth maxWidth="sm">
                <DialogTitle sx={{ pb: 1 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                        <Box>
                            <Typography sx={{ fontWeight: 950 }}>{selectedFactory?.factoryName || "Fabryka"}</Typography>
                        </Box>
                        <IconButton onClick={closeFactoryModal}>
                            <CloseRounded />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={1.5} sx={{ pt: 1 }}>
                        <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
                            <TextField
                                label="Początek wolnego"
                                type="date"
                                value={factoryDraft.breakStartDate}
                                onChange={(event) => setFactoryDraft((current) => ({ ...current, breakStartDate: event.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                            <TextField
                                label="Koniec wolnego"
                                type="date"
                                value={factoryDraft.breakEndDate}
                                onChange={(event) => setFactoryDraft((current) => ({ ...current, breakEndDate: event.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </Stack>
                        <TextField
                            label="Zamówienia przyjmują do"
                            type="date"
                            value={factoryDraft.orderDeadlineDate}
                            onChange={(event) => setFactoryDraft((current) => ({ ...current, orderDeadlineDate: event.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <TextField
                            label="Wiadomość"
                            value={factoryDraft.message}
                            onChange={(event) => setFactoryDraft((current) => ({ ...current, message: event.target.value }))}
                            multiline
                            minRows={4}
                            fullWidth
                            placeholder="Np. Zamówienia z gwarancją realizacji świątecznej przyjmujemy do 11 listopada."
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
                    <Button variant="outlined" color="inherit" onClick={clearFactoryEventData} disabled={isSaving}>
                        Wyczyść
                    </Button>
                    <Button variant="outlined" onClick={closeFactoryModal}>
                        Anuluj
                    </Button>
                    <Button variant="contained" startIcon={<SaveRounded />} onClick={saveFactoryEventData} disabled={isSaving} sx={{ fontWeight: 850 }}>
                        Zapisz
                    </Button>
                </DialogActions>
            </Dialog>
        </EmbeddedLayout>
    );
};

export default NonWorkingDays;
